import { useInfiniteQuery, useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { api } from '@/lib/api-client'
import { triggerMeta } from '@/lib/automation/catalog'

// Danh mục trigger/action/toán tử/biến nằm ở '@/lib/automation/catalog'.

// ─── Kiểu dữ liệu flowConfig (DAG) ────────────────────────────────────

export interface XY {
  x: number
  y: number
}

/** position là mở rộng của eCDP để lưu vị trí canvas, backend giữ nguyên JSON. */
export interface FlowTrigger {
  id: string
  type: string
  label: string
  config: Record<string, unknown>
  position?: XY
}

export type FlowNodeKind = 'action' | 'condition' | 'delay' | 'note'

export interface FlowNode {
  id: string
  type: FlowNodeKind
  actionType?: string
  label: string
  config: Record<string, unknown>
  status: 'active' | 'wip' | 'disabled'
  position?: XY
  branches?: { true: string[]; false: string[] }
}

export interface FlowEdge {
  source: string
  target: string
  label?: string
}

export interface FlowConfig {
  version: string
  trigger: FlowTrigger
  nodes: FlowNode[]
  edges: FlowEdge[]
  metadata?: Record<string, unknown>
}

// ─── Kiểu dữ liệu rule ────────────────────────────────────────────────

export interface AutomationRule {
  id: string
  orgId: string
  name: string
  description: string | null
  trigger: string
  conditions: unknown[]
  actions: unknown[]
  enabled: boolean
  priority: number
  flowVersion: number
  flowConfig: FlowConfig | null
  runCount: number
  lastRunAt: string | null
  /** Kết quả lượt chạy thật gần nhất (null = chưa chạy / trước khi có cột này). */
  lastRunStatus?: 'ok' | 'partial' | 'error' | 'waiting' | null
  createdAt: string
  updatedAt: string
}

export interface CreateRuleInput {
  name: string
  trigger: string
  description?: string
  priority?: number
  enabled?: boolean
  flowVersion?: number
  flowConfig?: FlowConfig
}

// ─── Kết quả chạy (khớp automation-types.ts ở backend) ────────────────

export interface NodeExecutionResult {
  nodeId: string
  nodeLabel: string
  status: 'success' | 'skipped' | 'error' | 'wip'
  durationMs: number
  error?: string
  branchTaken?: 'true' | 'false'
  output?: Record<string, unknown>
}

export interface FlowExecutionResult {
  ruleId: string
  ruleName: string
  runId: string
  totalNodes: number
  executedNodes: number
  skippedNodes: number
  errorNodes: number
  nodeResults: NodeExecutionResult[]
  durationMs: number
  dryRun?: boolean
  waiting?: boolean
  /** Không thoả lọc "Chỉ khi" của kích hoạt — luồng không chạy. */
  filtered?: boolean
  filterReason?: string
}

export interface TestRunResult extends FlowExecutionResult {
  testContext: {
    contactId: string | null
    conversationId: string | null
    usedSampleData: boolean
    contactName: string | null
    contactPhone: string | null
    messageText: string | null
    triggerData: Record<string, unknown> | null
    triggerType: string
  }
}

/** details của một dòng ActivityLog automation (backend automation-engine.ts). */
export interface RunLogDetails {
  runId?: string
  segment?: number
  waiting?: boolean
  filtered?: boolean
  filterReason?: string
  trigger?: string
  isTestRun?: boolean
  durationMs?: number
  nodeResults?: NodeExecutionResult[]
  context?: {
    conversationId?: string | null
    contactId?: string | null
    contactName?: string | null
    contactPhone?: string | null
    messageText?: string | null
    triggerData?: Record<string, unknown> | null
  }
}

export interface RunLogRow {
  id: string
  ruleId: string
  ruleName: string
  /** flow_executed | flow_resumed | flow_filtered | test_run */
  action: string
  runId: string
  segment: number
  waiting: boolean
  metadata: RunLogDetails | null
  createdAt: string
}

export interface MessageTemplate {
  id: string
  name: string
  content: string
  category: string | null
  isPersonal: boolean
}

// ─── Query hooks ──────────────────────────────────────────────────────

export const automationKeys = {
  rules: ['automation-rules'] as const,
  rule: (id: string | undefined) => ['automation-rule', id] as const,
  runLogs: (ruleId: string | undefined) => ['automation', 'run-logs', ruleId ?? 'all'] as const,
  templates: ['automation', 'templates'] as const,
}

/** `enabled` = false → không gọi (vd hộp quy tắc nhãn chưa mở, hoặc người xem không có quyền `automation.view`). */
export function useAutomationRules(enabled = true) {
  return useQuery<{ rules: AutomationRule[] }>({
    queryKey: automationKeys.rules,
    enabled,
    queryFn: async () => {
      const { data } = await api.get('/automation/rules')
      return data
    },
  })
}

export function useAutomationRule(id: string | undefined) {
  return useQuery<AutomationRule>({
    queryKey: automationKeys.rule(id),
    queryFn: async () => (await api.get<AutomationRule>(`/automation/rules/${id}`)).data,
    enabled: !!id,
  })
}

export interface BoLocNhatKyServer {
  /** Thời điểm ISO — lấy các đoạn chạy từ lúc này (bao gồm). */
  from?: string
  /** Thời điểm ISO — tới trước lúc này (không bao gồm). */
  to?: string
  /** that = chạy thật (kể cả lượt bị lọc), thu = chạy thử. */
  loai?: 'that' | 'thu'
}

/**
 * Nhật ký chạy, mới nhất trước, 200 đoạn mỗi trang (mỗi đoạn một dòng — gộp
 * theo runId bằng lib/automation/run-result). `fetchNextPage` lấy đoạn cũ hơn.
 */
export function useRunLogs(ruleId: string | undefined, loc: BoLocNhatKyServer = {}, enabled = true) {
  return useInfiniteQuery({
    queryKey: [...automationKeys.runLogs(ruleId), loc],
    enabled,
    initialPageParam: null as string | null,
    queryFn: async ({ pageParam }) => (await api.get<{ logs: RunLogRow[]; nextCursor: string | null }>('/automation/run-logs', {
      params: { limit: 200, ...(ruleId ? { ruleId } : {}), ...loc, ...(pageParam ? { cursor: pageParam } : {}) },
    })).data,
    getNextPageParam: (trang) => trang.nextCursor,
  })
}

export interface ManualRule {
  id: string
  name: string
  description: string | null
  buttonLabel: string
}

/** Luồng "Nhân viên bấm nút" đang bật — cho nút trên hồ sơ khách. Không có quyền → lỗi 403, ẩn nút. */
export function useManualRules() {
  return useQuery<ManualRule[]>({
    queryKey: ['automation', 'manual-rules'],
    retry: false,
    staleTime: 60_000,
    queryFn: async () => (await api.get<{ rules: ManualRule[] }>('/automation/manual-rules')).data.rules,
  })
}

/** Chạy THẬT một luồng thủ công với một khách. */
export function useRunManualRule() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: async (vars: { ruleId: string; contactId: string }) =>
      (await api.post<FlowExecutionResult>(`/automation/rules/${vars.ruleId}/run`, { contactId: vars.contactId })).data,
    onSuccess: (_d, vars) => {
      qc.invalidateQueries({ queryKey: automationKeys.runLogs(vars.ruleId) })
      qc.invalidateQueries({ queryKey: automationKeys.rules })
    },
  })
}

export function useMessageTemplates(enabled = true) {
  return useQuery<MessageTemplate[]>({
    queryKey: automationKeys.templates,
    enabled,
    staleTime: 60_000,
    queryFn: async () => (await api.get<{ templates: MessageTemplate[] }>('/automation/templates')).data.templates,
  })
}

// ─── Mutation hooks ───────────────────────────────────────────────────

export function useCreateRule() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: async (input: CreateRuleInput) => {
      const { data } = await api.post<AutomationRule>('/automation/rules', input)
      return data
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: automationKeys.rules }),
  })
}

export function useUpdateRule() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: async (vars: { id: string; data: Partial<CreateRuleInput> & { flowConfig?: FlowConfig } }) => {
      const { data } = await api.put<AutomationRule>(`/automation/rules/${vars.id}`, vars.data)
      return data
    },
    onSuccess: (rule, vars) => {
      qc.invalidateQueries({ queryKey: automationKeys.rules })
      qc.setQueryData(automationKeys.rule(vars.id), rule)
    },
  })
}

export function useDeleteRule() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: async (id: string) => {
      const { data } = await api.delete(`/automation/rules/${id}`)
      return data
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: automationKeys.rules }),
  })
}

/**
 * Nhân bản rule: tạo rule mới (đang tắt) từ flowConfig hiện có. Backend tự bỏ
 * token webhook của bản gốc nên bản sao có URL riêng.
 */
export function useDuplicateRule() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: async (rule: AutomationRule) => {
      const input: CreateRuleInput = {
        name: `${rule.name} (bản sao)`, // i18n-ignore: tên luồng lưu vào DB
        trigger: rule.trigger,
        description: rule.description ?? undefined,
        priority: rule.priority,
        enabled: false,
        flowVersion: 2,
        flowConfig: rule.flowConfig ? structuredClone(rule.flowConfig) : emptyFlowConfig(rule.trigger),
      }
      return (await api.post<AutomationRule>('/automation/rules', input)).data
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: automationKeys.rules }),
  })
}

/**
 * Chạy thử flow đang ở canvas (chưa cần lưu). `dryRun` mặc định: backend chỉ
 * mô phỏng action — không gửi tin, không ghi dữ liệu khách.
 */
export function useTestRun(ruleId: string | undefined) {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: async (body: { flowConfig: FlowConfig; contactId?: string; conversationId?: string }) =>
      (await api.post<TestRunResult>(`/automation/rules/${ruleId ?? '__unsaved__'}/test-run`, { ...body, dryRun: true })).data,
    onSuccess: () => qc.invalidateQueries({ queryKey: automationKeys.runLogs(ruleId) }),
  })
}

// ─── Tiện ích ─────────────────────────────────────────────────────────

/** Tạo flowConfig rỗng với 1 node trigger. */
export function emptyFlowConfig(trigger: string): FlowConfig {
  return {
    version: '2',
    trigger: {
      id: 'trigger',
      type: trigger,
      label: triggerMeta(trigger).ten,
      config: {},
      position: { x: 40, y: 40 },
    },
    nodes: [],
    edges: [],
  }
}

let idCounter = 0
export function genNodeId(prefix = 'node'): string {
  idCounter += 1
  return `${prefix}_${Date.now().toString(36)}_${idCounter}`
}

// ── Webhook token cho trigger 'webhook' ────────────────────────────
export interface WebhookTokenInfo {
  token: string
  url: string
  /** Payload của yêu cầu mẫu gần nhất — để liệt kê "các trường nhận được". */
  lastPayload: Record<string, unknown> | null
  lastReceivedAt: string | null
}

/** Lấy (tạo nếu chưa có) URL webhook của một rule. Chỉ gọi khi rule đã lưu. */
export function useWebhookToken(ruleId: string | undefined, enabled: boolean) {
  return useQuery<WebhookTokenInfo>({
    queryKey: ['automation', 'webhook-token', ruleId],
    enabled: !!ruleId && enabled,
    queryFn: async () => (await api.get<WebhookTokenInfo>(`/automation/rules/${ruleId}/webhook-token`)).data,
  })
}

/** Xoay token webhook (URL cũ hết hiệu lực). */
export function useRotateWebhookToken(ruleId: string | undefined) {
  const qc = useQueryClient()
  return useMutation<WebhookTokenInfo, unknown, void>({
    mutationFn: async () => (await api.post<WebhookTokenInfo>(`/automation/rules/${ruleId}/webhook-token`)).data,
    onSuccess: (data) => qc.setQueryData(['automation', 'webhook-token', ruleId], data),
  })
}

// ── Field theo kết nối (Evotech hạng/điểm…) ────────────────────────
export interface ContactAttributeField {
  field: string
  source: string
  sourceLabel: string
  label: string
}

/** Field `contact.<nguồn>_<khoá>` tổ chức ĐÃ có dữ liệu thật — gợi ý trong ô chọn field Điều kiện + nút "Chèn biến". */
export function useContactAttributeFields(enabled = true) {
  return useQuery<{ fields: ContactAttributeField[] }>({
    queryKey: ['automation', 'contact-attribute-fields'],
    enabled,
    queryFn: async () => (await api.get<{ fields: ContactAttributeField[] }>('/automation/contact-attribute-fields')).data,
    staleTime: 5 * 60_000,
  })
}
